/**
 * Delivery Filter Controller
 * Handles filtering products by delivery type (hemleverans, container, hamtning)
 * Supports backward compatibility with old values (storsack, smasack)
 */

const DeliveryFilter = {
  currentFilter: 'all',
  filterCards: null,
  productCards: null,
  deliverySections: null,
  
  /**
   * Initialize delivery filter
   */
  init() {
    this.filterCards = document.querySelectorAll('.delivery-filter-card');
    this.productCards = document.querySelectorAll('.product-card');
    this.deliverySections = document.querySelectorAll('[data-delivery-section]');
    
    if (!this.filterCards || this.filterCards.length === 0) {
      return; // No filter cards found
    }
    
    // Get current filter from URL or localStorage
    this.currentFilter = this.getCurrentFilter();
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Apply initial filter
    this.applyFilter(this.currentFilter);
    this.updateActiveState(this.currentFilter);
  },
  
  /**
   * Map old filter values to new values for backward compatibility
   */
  mapOldToNew(filter) {
    const mapping = {
      'storsack': 'hemleverans',
      'smasack': 'hemleverans',
      'hamtning': 'hamtning' // No change
    };
    return mapping[filter] || filter;
  },
  
  /**
   * Get current filter from URL or localStorage
   */
  getCurrentFilter() {
    // Check URL query parameter first
    const urlParams = new URLSearchParams(window.location.search);
    const urlFilter = urlParams.get('delivery');
    if (urlFilter) {
      // Map old values to new values
      const mappedFilter = this.mapOldToNew(urlFilter);
      if (['all', 'hemleverans', 'container', 'hamtning'].includes(mappedFilter)) {
        return mappedFilter;
      }
    }
    
    // Check URL path (e.g., /collections/all/hemleverans)
    const pathParts = window.location.pathname.split('/').filter(p => p);
    const lastPart = pathParts[pathParts.length - 1];
    if (lastPart) {
      const mappedFilter = this.mapOldToNew(lastPart);
      if (['hemleverans', 'container', 'hamtning'].includes(mappedFilter)) {
        return mappedFilter;
      }
    }

    // The curated products page should always open with all delivery methods.
    if (window.location.pathname === '/pages/products' || window.location.pathname === '/pages/products/') {
      return 'all';
    }
    
    // Check localStorage
    const savedFilter = this.getSavedFilter();
    if (savedFilter) {
      const mappedFilter = this.mapOldToNew(savedFilter);
      if (['all', 'hemleverans', 'container', 'hamtning'].includes(mappedFilter)) {
        return mappedFilter;
      }
    }
    
    return 'all';
  },
  
  /**
   * Set up event listeners on filter cards
   */
  setupEventListeners() {
    this.filterCards.forEach(card => {
      card.addEventListener('click', (e) => {
        e.preventDefault();
        const filter = card.dataset.filter || 'all';
        this.setFilter(filter);
      });
    });
  },
  
  /**
   * Set active filter
   */
  setFilter(filter) {
    // Map old values to new values
    const mappedFilter = this.mapOldToNew(filter);
    
    if (!['all', 'hemleverans', 'container', 'hamtning'].includes(mappedFilter)) {
      console.warn('Invalid filter:', filter);
      return;
    }
    
    this.currentFilter = mappedFilter;
    
    // Update URL with query parameter
    this.updateURL(this.currentFilter);
    
    // Save to localStorage
    this.saveFilter(this.currentFilter);
    
    // Apply filter
    this.applyFilter(this.currentFilter);
    
    // Update active state
    this.updateActiveState(this.currentFilter);
  },
  
  /**
   * Apply filter to products
   */
  applyFilter(filter) {
    this.updateDeliverySections(filter);
    this.updateShippingBanner(filter);

    if (!this.productCards || this.productCards.length === 0) {
      return; // No products found
    }
    
    let visibleCount = 0;
    
    this.productCards.forEach(card => {
      const deliveryType = card.dataset.deliveryType || '';
      // Map old delivery types to new values for matching
      const mappedDeliveryType = this.mapOldToNew(deliveryType);
      
      if (filter === 'all') {
        // Show all products
        card.style.display = '';
        visibleCount++;
      } else {
        // Show only products matching filter (support both old and new values)
        if (mappedDeliveryType === filter || deliveryType === filter) {
          card.style.display = '';
          visibleCount++;
        } else {
          card.style.display = 'none';
        }
      }
    });
    
    // Show/hide empty state
    this.updateEmptyState(visibleCount);
  },
  
  /**
   * Update URL with query parameter
   */
  updateURL(filter) {
    const url = new URL(window.location);
    
    if (filter === 'all') {
      // Remove delivery parameter
      url.searchParams.delete('delivery');
    } else {
      // Set delivery parameter
      url.searchParams.set('delivery', filter);
    }
    
    // Update URL without reloading page
    window.history.pushState({ filter }, '', url);
  },
  
  /**
   * Save filter to localStorage
   */
  saveFilter(filter) {
    try {
      localStorage.setItem('vedbiten_delivery_filter', filter);
    } catch (error) {
      console.warn('Could not save filter to localStorage:', error);
    }
  },
  
  /**
   * Get saved filter from localStorage
   */
  getSavedFilter() {
    try {
      return localStorage.getItem('vedbiten_delivery_filter');
    } catch (error) {
      console.warn('Could not get filter from localStorage:', error);
      return null;
    }
  },
  
  /**
   * Update active state on filter cards
   */
  updateActiveState(filter) {
    this.filterCards.forEach(card => {
      const cardFilter = card.dataset.filter || 'all';
      
      if (cardFilter === filter) {
        card.classList.add('delivery-filter-card--active');
        card.setAttribute('aria-current', 'page');
      } else {
        card.classList.remove('delivery-filter-card--active');
        card.removeAttribute('aria-current');
      }
    });
  },
  
  /**
   * Update empty state visibility
   */
  updateEmptyState(visibleCount) {
    const emptyState = document.querySelector('.collection-empty');
    const productsGrid = document.querySelector('.products-grid');
    
    if (emptyState && productsGrid) {
      if (visibleCount === 0) {
        emptyState.style.display = 'flex';
        productsGrid.style.display = 'none';
      } else {
        emptyState.style.display = 'none';
        productsGrid.style.display = 'grid';
      }
    }
  },

  /**
   * Show/hide whole delivery sections on the curated products page.
   */
  updateDeliverySections(filter) {
    if (!this.deliverySections || this.deliverySections.length === 0) {
      return;
    }

    this.deliverySections.forEach(section => {
      const sectionType = this.mapOldToNew(section.dataset.deliverySection || '');
      section.style.display = filter === 'all' || sectionType === filter ? '' : 'none';
    });
  },

  /**
   * Pickup does not need postal-code based shipping.
   */
  updateShippingBanner(filter) {
    const shippingBanner = document.getElementById('shipping-banner-section');
    if (!shippingBanner) {
      return;
    }

    shippingBanner.style.display = filter === 'hamtning' ? 'none' : '';
  }
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    DeliveryFilter.init();
  });
} else {
  DeliveryFilter.init();
}

// Handle browser back/forward buttons
window.addEventListener('popstate', (e) => {
  const filter = DeliveryFilter.getCurrentFilter();
  DeliveryFilter.applyFilter(filter);
  DeliveryFilter.updateActiveState(filter);
});

// Export for use in other scripts
window.DeliveryFilter = DeliveryFilter;

